import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L, { DomEvent } from 'leaflet';
import { LocateFixed, Navigation, AlertCircle } from 'lucide-react';

import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
	iconUrl: markerIcon,
	shadowUrl: markerShadow,
	iconSize: [25, 41],
	iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

interface MapProps {
	coords: { lat: number; lng: number };
	setCoords: (coords: { lat: number; lng: number }) => void;
	onClose?: () => void;
	radius?: number;
	posts?: any[];
	simplified?: boolean;
}

const postIcon = L.divIcon({
	className: 'custom-post-marker',
	html: `<div style="background-color:#ef4444;width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
	iconSize: [14, 14],
	iconAnchor: [7, 7],
});

const LocationPicker = ({ setCoords }: { setCoords: any }) => {
	const downPos = useRef<{ x: number; y: number } | null>(null);
	useMapEvents({
		mousedown(e) {
			downPos.current = { x: e.originalEvent.clientX, y: e.originalEvent.clientY };
		},
		click(e) {
			if (downPos.current) {
				const dx = e.originalEvent.clientX - downPos.current.x;
				const dy = e.originalEvent.clientY - downPos.current.y;
				if (Math.sqrt(dx * dx + dy * dy) > 5) return;
			}
			setCoords({ lat: e.latlng.lat, lng: e.latlng.lng });
		},
	});
	return null;
};

// radius prop is a level (1=1km, 2=10km, 3=100km, 4=500km)
const LEVEL_TO_M: Record<number, number> = { 1: 1_000, 2: 10_000, 3: 100_000, 4: 500_000 };
const levelToMeters = (level: number) => LEVEL_TO_M[level] ?? 1_000;

const flyToCircle = (map: L.Map, lat: number, lng: number, radius: number) => {
	if (!map) return;
	try {
		const r = levelToMeters(radius);
		const bounds = L.latLng(lat, lng).toBounds(2 * r);
		if (bounds?.isValid()) {
			map.flyToBounds(bounds, { animate: true, duration: 0.5, padding: [28, 28] });
		}
	} catch (e) {
		console.error('flyToCircle', e);
	}
};

const FlyToBounds = ({ lat, lng, radius, once }: { lat: number; lng: number; radius: number; once?: boolean }) => {
	const map = useMap();
	const hasFlown = useRef(false);

	useEffect(() => {
		if (once && hasFlown.current) return;
		flyToCircle(map, lat, lng, radius);
		hasFlown.current = true;
	}, [lat, lng, radius, map, once]);
	return null;
};

const DynamicCircle = ({ lat, lng, radiusM }: { lat: number; lng: number; radiusM: number }) => {
	const [hidden, setHidden] = useState(false);

	useMapEvents({
		zoomstart: () => setHidden(true),
		zoomend: () => setHidden(false),
	});

	if (hidden) return null;

	return (
		<Circle
			center={[lat, lng]}
			radius={radiusM}
			pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.1, weight: 2, className: 'non-scaling-stroke' }}
			interactive={false}
		/>
	);
};

// ── Toast (portal → renders into document.body, escapes overflow:hidden) ──
const Toast = ({ message, onDone }: { message: string; onDone: () => void }) => {
	useEffect(() => {
		const t = setTimeout(onDone, 3500);
		return () => clearTimeout(t);
	}, [onDone]);

	return ReactDOM.createPortal(
		<div
			style={{
				position: 'fixed',
				bottom: 28,
				left: '50%',
				transform: 'translateX(-50%)',
				zIndex: 99999,
				background: '#1e293b',
				color: 'white',
				padding: '10px 20px',
				borderRadius: 12,
				fontSize: 13,
				fontWeight: 500,
				boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
				whiteSpace: 'nowrap',
				display: 'flex',
				alignItems: 'center',
				gap: 8,
				animation: 'mapToastIn 0.25s ease',
			}}
		>
			<style>{`@keyframes mapToastIn { from { opacity:0; transform:translateX(-50%) translateY(8px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
			<AlertCircle size={16} style={{ color: '#f87171', flexShrink: 0 }} />
			{message}
		</div>,
		document.body
	);
};

// ── Main component ─────────────────────────────────────────────────────────
const ExploreMap: React.FC<MapProps> = ({ coords, setCoords, onClose, radius = 1, posts = [], simplified = false }) => {
	const mapRef = useRef<L.Map | null>(null);
	const btnContainerRef = useRef<HTMLDivElement | null>(null);

	// 'granted' | 'denied' | 'prompt' | 'unavailable'
	const [geoPermission, setGeoPermission] = useState<string>('prompt');
	const [toast, setToast] = useState<{ msg: string; id: number } | null>(null);

	// Query permission status on mount and watch for changes
	useEffect(() => {
		if (!navigator.permissions) return;
		navigator.permissions
			.query({ name: 'geolocation' })
			.then((status) => {
				setGeoPermission(status.state);
				status.onchange = () => setGeoPermission(status.state);
			})
			.catch(() => { });
	}, []);

	const lat = typeof coords?.lat === 'number' && !isNaN(coords.lat) ? coords.lat : 25;
	const lng = typeof coords?.lng === 'number' && !isNaN(coords.lng) ? coords.lng : 77;
	const radiusM = levelToMeters(radius);
	const safePosts = Array.isArray(posts) ? posts : [];

	const latRef = useRef(lat);
	const lngRef = useRef(lng);
	const radiusRef = useRef(radius);
	latRef.current = lat;
	lngRef.current = lng;
	radiusRef.current = radius;

	useEffect(() => {
		const el = btnContainerRef.current;
		if (!el) return;
		DomEvent.disableClickPropagation(el);
		DomEvent.disableScrollPropagation(el);
	}, []);

	const showToast = useCallback((msg: string) => {
		setToast({ msg, id: Date.now() });
	}, []);

	const recenterPin = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		const map = mapRef.current;
		if (!map) return;
		flyToCircle(map, latRef.current, lngRef.current, radiusRef.current);
	};

	const moveToUserLocation = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		if (!navigator.geolocation) {
			showToast('Geolocation is not supported by your browser.');
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const newLat = pos.coords.latitude;
				const newLng = pos.coords.longitude;
				setGeoPermission('granted');
				setCoords({ lat: newLat, lng: newLng });
				const map = mapRef.current;
				if (map && newLat === latRef.current && newLng === lngRef.current) {
					flyToCircle(map, newLat, newLng, radiusRef.current);
				}
			},
			(err) => {
				console.error(err);
				setGeoPermission('denied');
				showToast('Location access denied. Please allow it in your browser settings.');
			},
			{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
		);
	};

	const btnStyle: React.CSSProperties = {
		width: 40,
		height: 40,
		borderRadius: 10,
		background: 'white',
		border: 'none',
		outline: 'none',
		color: '#004a99',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
		padding: 0,
		position: 'relative',
	};

	const geoDenied = geoPermission === 'denied';

	return (
		<div
			style={{
				position: 'relative',
				width: '100%',
				height: simplified ? '100%' : 'auto',
				aspectRatio: simplified ? 'auto' : '1/1',
				borderRadius: 16,
				overflow: 'hidden',
				marginBottom: simplified ? 0 : 20,
				boxShadow: simplified ? 'none' : '0 4px 12px rgba(0,0,0,0.15)',
				border: simplified ? 'none' : '2px solid #004a99',
			}}
		>
			<style>{`
                .leaflet-control-zoom { border:none!important; border-radius:12px!important; overflow:hidden!important; box-shadow:0 4px 12px rgba(0,0,0,0.15)!important; margin-top:15px!important; margin-left:15px!important; }
                .leaflet-control-zoom-in,.leaflet-control-zoom-out { background-color:white!important; color:#004a99!important; border:none!important; outline:none!important; font-weight:bold!important; display:flex!important; align-items:center!important; justify-content:center!important; }
                .leaflet-bar a { border:none!important; }
                .leaflet-control-zoom-in:hover,.leaflet-control-zoom-out:hover { background-color:#f0f7ff!important; }
                .leaflet-container { cursor: grab !important; }
                .leaflet-container:active { cursor: grabbing !important; }
                .leaflet-container svg path { cursor: grab !important; }
                .non-scaling-stroke { vector-effect: non-scaling-stroke; }
            `}</style>

			<MapContainer
				ref={mapRef}
				center={[lat, lng]}
				zoom={13}
				style={{ height: '100%', width: '100%' }}
				doubleClickZoom={false}
			>
				<TileLayer
					url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					updateWhenZooming={false}
					updateWhenIdle={true}
				/>
				<Marker position={[lat, lng]} interactive={false} keyboard={false} />
				{!simplified && (
					<DynamicCircle lat={lat} lng={lng} radiusM={radiusM} />
				)}
				{safePosts.map((post) => {
					if (!post?.location) return null;
					const m = post.location.match(/\(([^,]+),\s*([^)]+)\)/);
					if (!m) return null;
					const pLat = parseFloat(m[1]),
						pLng = parseFloat(m[2]);
					if (isNaN(pLat) || isNaN(pLng)) return null;
					return (
						<Marker
							key={post.id ?? Math.random()}
							position={[pLat, pLng]}
							icon={postIcon}
							interactive={false}
							keyboard={false}
						/>
					);
				})}
				<LocationPicker setCoords={setCoords} />
				<FlyToBounds lat={lat} lng={lng} radius={radius} once={simplified} />
			</MapContainer>

			{/* Outside MapContainer → normal pointer events */}
			{!simplified && (
				<div
					ref={btnContainerRef}
					style={{
						position: 'absolute',
						bottom: 20,
						right: 20,
						zIndex: 1000,
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
					}}
				>
					{/* Navigate to location button — shows red ! badge when permission denied */}
					<button onClick={moveToUserLocation} style={btnStyle} title='Move pin to My Location'>
						<Navigation size={20} strokeWidth={2.5} />
						{geoDenied && (
							<span
								title='Location access denied'
								style={{
									position: 'absolute',
									top: -5,
									right: -5,
									width: 16,
									height: 16,
									borderRadius: '50%',
									background: '#ef4444',
									color: 'white',
									fontSize: 10,
									fontWeight: 900,
									lineHeight: '16px',
									textAlign: 'center',
									border: '1.5px solid white',
									pointerEvents: 'auto',
									cursor: 'help',
								}}
							>
								!
							</span>
						)}
					</button>
					<button onClick={recenterPin} style={btnStyle} title='Recenter map on Pin'>
						<LocateFixed size={20} strokeWidth={2.5} />
					</button>
					{onClose && (
						<button
							onClick={onClose}
							style={{
								borderRadius: 50,
								fontWeight: 'bold',
								padding: '6px 12px',
								background: '#1e3a5f',
								color: 'white',
								border: 'none',
								cursor: 'pointer',
							}}
						>
							Done
						</button>
					)}
				</div>
			)}

			{/* Toast */}
			{toast && <Toast key={toast.id} message={toast.msg} onDone={() => setToast(null)} />}
		</div>
	);
};

export default ExploreMap;
